import { Dropdown, GoogleMapModal, MultiselectDropdown, RadioButton, Stepper, TextInput } from '@components/common';
import { useStepperStore } from '@store/stepperStore';
import { useFormik, getIn } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defaultAddSiteLeadFormInitialValues, substratesOptions } from '.';
import { brandUsedList, glueUsedRadioTypeList, typeOfContractor } from '@config/constant';
import { Modal } from '@components/common/modal/modal';
import { useGoogleMapStore } from '@store/googleMapStore';
import { useValidation } from '@hooks/validations';
import { useFetchGrades } from '@services/hooks/competition-conversion/useFetchGrades';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useCompetitionConversionUsageQuery } from '@services/hooks/competition-conversion/useCompetitionConversionUsageQuery';
import { useSiteLeadStore } from '@store/siteLeadStore';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteLeadQuery, useUpdatedNewSiteLead, useUpdateKAESSNewLead } from '@services/hooks/site-leads';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { isNullOrEmpty, parseMultiselectOptions } from '@components/delaerVisit/components/addEditDealerVisit';
import { useAddSiteLeadBySS } from '@services/hooks/site-leads/useAddSiteLeadBySS';
import { ToastType, useTosterStore } from '@store/toster';
import { useLocation } from 'react-router-dom';
import { usePhoneNumberValidateQuery } from '@services/hooks/enduser-details/usePhoneNumberValidateQuery';
import { useDebounce } from '@hooks/useDebounce';
import { useContractorDetailsQuery } from '@services/hooks/site-leads/site-leads-ss/userContractorDetails';
import { IContractorDetailsResponse } from '@services/hooks/site-leads/site-leads-ss';
import { ContractorSSDetailsModal } from '@components/common/contractorSSDetails/ContractorSSDetails';
import { ProfileData } from '@services/hooks/profile';
import { useGetRxDb } from '@hooks/getRxdbData';
import { SiteSummaryDetails } from '@services/hooks/contractor360';
import { processSecondaryCompetitionReason, parseSecondaryCompetitionReasonFromProposedField } from '@helper/utils';

export const KEASSAddNewSite = (): ReactElement => {
	useSetMainHeader('Add New Site');
	const { state } = useLocation();
	const [isContractorModalOpen, setIsContractorModalOpen] = useState(false);
	const { setCurrentStep } = useStepperStore();
	const { currentAddress } = useGoogleMapStore();
	const { AddKAESSSiteLeadValidationSchema } = useValidation();
	const [locationModal, setLocationModal] = useState<boolean>(false);
	const { data: gradeList, error: gradeListError, isPending: isGradeListPending } = useFetchGrades();
	const { sfid, setKeyBaseValue, siteDetails, setSiteLeadId } = useSiteLeadStore();
	const {
		data: competitionList,
		error: competitionListError,
		isPending: iscompetitionListPending,
	} = useCompetitionConversionUsageQuery();
	const { data: siteData, isFetching: isSiteDataFetching, error: siteDataError } = useSiteLeadQuery();
	const { mutate: updateSiteLead } = useUpdatedNewSiteLead();
	const { mutate: updateKAESSNewLead } = useUpdateKAESSNewLead();
	const { mutate: postAddSiteLeadBySSData } = useAddSiteLeadBySS();
	const { setToaster } = useTosterStore();
	const {
		mutate: fetchContractorDetails,
		isPending: isContractorDetailsPending,
		isError: isContractorDetailsError,
	} = useContractorDetailsQuery();
	const [contractorDetailsResult, setContractorDetailsResult] = useState<IContractorDetailsResponse | null>(null);
	const [contractorAppIdC, setContractorAppIdC] = useState<string | undefined>(undefined);
	const [contractorLeadId, setContractorLeadId] = useState<string | undefined>(undefined);
	const [endUserC, setEndUserC] = useState<boolean>(false);
	const [isContractorCreated, setIsContractorCreated] = useState<boolean>(false);
	const { result: profileData } = useGetRxDb<ProfileData>('profile');
	const [contractorStatusMessage, setContractorStatusMessage] = useState<string>('');
	const [latitude, setLatitude] = useState<number | null>(null);
	const [longitude, setLongitude] = useState<number | null>(null);
	const [isLocationFromMap, setIsLocationFromMap] = useState<boolean>(false);

	const formik = useFormik({
		initialValues: defaultAddSiteLeadFormInitialValues,
		onSubmit: () => {},
		validationSchema: AddKAESSSiteLeadValidationSchema,
	});

	const [contractorNumber, setContractorNumber] = useState(formik.values.contractorNumber ?? '');
	const {
		refetch: refetchPhoneValidation,
		data: phoneValidationData,
		isFetching: isPhoneValidationFetching,
	} = usePhoneNumberValidateQuery(contractorNumber, 'contractorNumber', {
		enabled: contractorNumber.length === 10,
	});

	const [maxLimit, setMaxLimit] = useState<number>(3);

	const debouncedUpdateContractor = useDebounce((value: string) => {
		formik.setFieldValue('contractorNumber', value);
		setContractorNumber(value);

		if (value.length === 10) {
			refetchPhoneValidation?.();
		}
	}, 300);

	const handlePrimaryNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 10);

		// Update local state immediately for UI responsiveness
		setContractorNumber(value);

		// Debounced update for formik and API call
		debouncedUpdateContractor(value);
	};

	const updateSiteLeadData = useCallback((): void => {
		// Determine which contractor fields to use based on current stage
		const isPendingContractorValidation = siteData?.current_stage_of_site_c === 'Pending Contractor Validation';

		// Process secondaryCompetitionReason: replace "Other" with custom text value
		const processedSecondaryCompetitionReason = processSecondaryCompetitionReason(
			formik.values.secondaryCompetitionReason,
			formik.values.secondaryCompetitionReasonOther
		);

		const payload = {
			glue_used_at_the_site: formik.values.glueUsedAtSite,
			grade_glue_used_at_the_site: formik.values.gradeUsed,
			brand_of_glue_used_at_site: formik.values.glueUsedAtSite === 'Competition' ? formik.values.brandUsed : '',
			reason_of_competition: formik.values.competitionReason,
			type_of_contract:
				typeOfContractor.find((contractor) => contractor.value === formik.values.contractType)?.name ?? '',
			roff_substrate_c: isNullOrEmpty(formik.values.substratesCombinations),
			proposed_field_competition_sites: isNullOrEmpty(processedSecondaryCompetitionReason),
			...(isPendingContractorValidation
				? {
						lead_app_id_c: siteData?.lead_app_id_c,
					}
				: {
						end_user_c: siteData?.end_user_c ? siteData?.end_user_c : '',
					}),
		};

		if (siteData?.id) {
			// Use the KAESS-specific update mutation
			updateKAESSNewLead?.(payload, {
				onSuccess: (data) => {
					setToaster(true, ToastType.success, 'Site lead updated successfully');
					setCurrentStep(1);
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message || 'Failed to update site lead');
				},
			});
			return;
		}

		updateSiteLead?.(payload);
		setCurrentStep(1);
	}, [
		formik.values,
		siteDetails?.sfid,
		updateSiteLead,
		updateKAESSNewLead,
		siteData?.current_stage_of_site_c,
		setToaster,
	]);

	// Helper function to create key base values
	const createKeyBaseValues = useCallback(
		(
			formValues: typeof formik.values,
			contractorTypeValue: string,
			reasonForCompetition: string,
			latValue?: number | null,
			lngValue?: number | null,
			isEditMode?: boolean
		) => {
			const finalLat = latValue !== undefined ? latValue : latitude;
			const finalLng = lngValue !== undefined ? lngValue : longitude;
			const baseValues = {
				grade_glue_used_at_the_site: formValues.gradeUsed,
				brandUsed: formValues.brandUsed,
				type_of_contract: contractorTypeValue,
				reason: reasonForCompetition,
				glue_used_at_the_site: formValues.glueUsedAtSite,
				name: formValues.siteName,
				site_location_c: formValues.siteLocation,
				roff_substrate_c: isNullOrEmpty(formValues.substratesCombinations),
				reason_of_competition: '',
				market: 'BV',
				brand_of_glue_used_at_site: '',
				end_user_c: '0011s00001w9pGDAAY',
				supervisor_name: formValues.superVisorName ?? '',
				supervisor_phone_number: formValues.superVisorNumber ?? '',
				contract_phone: formValues.contractorNumber,
				contract_name: formValues.contractorName,
				...(contractorAppIdC && !siteData?.id && !sfid ? { lead_app_id_c: contractorAppIdC } : {}),
			};

			// Only include latitude and longitude for new sites, not in edit mode
			if (!isEditMode) {
				return {
					...baseValues,
					latitude: finalLat,
					longitude: finalLng,
				};
			}

			return baseValues;
		},
		[contractorAppIdC, siteData?.id, sfid, latitude, longitude]
	);

	// Helper function to get contractor field mapping
	const getContractorFieldMapping = useCallback(
		(
			contractorName: string,
			contractorNumber: string
		): {
			supervisor_name: string;
			supervisor_phone_number: string;
			contract_name: string;
			contract_phone: string;
			lead_contractor_name?: string;
			lead_contractor_phone?: string;
		} => {
			// Case 1: Contractor's mobile number does not exist (new contractor)
			// Case 2: Contractor exists with "Lead" status
			// Both cases should use lead_contractor_name and lead_contractor_phone
			if (isContractorCreated || !endUserC) {
				return {
					lead_contractor_name: contractorName || '',
					lead_contractor_phone: contractorNumber || '',
					supervisor_name: '',
					supervisor_phone_number: '',
					contract_name: '',
					contract_phone: '',
				};
			}

			// Case 3: Contractor exists with "Account" status
			// Should use contract_name and contract_phone
			if (endUserC) {
				return {
					supervisor_name: '',
					supervisor_phone_number: '',
					contract_name: contractorName || '',
					contract_phone: contractorNumber || '',
				};
			}

			// Fallback (should not reach here)
			return {
				supervisor_name: '',
				supervisor_phone_number: '',
				contract_name: '',
				contract_phone: '',
			};
		},
		[isContractorCreated, endUserC]
	);

	// Helper function to create new site payload
	const createNewSitePayload = useCallback(
		(
			formValues: typeof formik.values,
			contractorTypeValue: string,
			contractorFields: ReturnType<typeof getContractorFieldMapping>,
			latValue?: number | null,
			lngValue?: number | null
		) => {
			const currentProfile = profileData?.[0];
			const ssSfid = currentProfile?.sfid || '';
			const cmdiCode = currentProfile?.mdicode_c || '';
			const finalLat = latValue !== undefined ? latValue : latitude;
			const finalLng = lngValue !== undefined ? lngValue : longitude;

			// Process secondaryCompetitionReason: replace "Other" with custom text value
			const processedSecondaryCompetitionReason = processSecondaryCompetitionReason(
				formValues.secondaryCompetitionReason,
				formValues.secondaryCompetitionReasonOther
			);

			return {
				site_area: formValues.siteArea ?? '',
				site_location: formValues.siteLocation ?? '',
				name: formValues.siteName ?? '',
				...contractorFields,
				supervisor_name: formValues.superVisorName ?? '',
				supervisor_phone_number: formValues.superVisorNumber ?? '',
				...(isContractorCreated && contractorAppIdC ? { lead_app_id_c: contractorAppIdC } : {}),
				ss_sfid: ssSfid,
				cmdi_code_c: cmdiCode,
				glue_used_at_the_site: formValues.glueUsedAtSite ?? '',
				grade_glue_used_at_the_site: formValues.gradeUsed ?? '',
				...(contractorLeadId && { lead_app_id_c: contractorLeadId }),
				type_of_contract: contractorTypeValue ?? '',
				roff_substrate: isNullOrEmpty(formValues.substratesCombinations) ?? '',
				reason_of_competition: formValues.competitionReason ?? '',
				brand_of_glue_used_at_site: formik.values.glueUsedAtSite === 'Competition' ? formik.values.brandUsed : '',
				proposed_field_competition_sites: isNullOrEmpty(processedSecondaryCompetitionReason),
				latitude: finalLat,
				longitude: finalLng,
				...(!isContractorCreated && contractorAppIdC ? { end_user_c: contractorAppIdC } : {}),
			};
		},
		[profileData, isContractorCreated, contractorAppIdC, contractorLeadId, latitude, longitude]
	);

	// Helper function to handle new site creation
	const handleNewSiteCreation = useCallback(
		(
			formValues: typeof formik.values,
			contractorTypeValue: string,
			latValue?: number | null,
			lngValue?: number | null
		) => {
			const contractorFields = getContractorFieldMapping(
				formValues.contractorName ?? '',
				formValues.contractorNumber ?? ''
			);
			const newSitePayload = createNewSitePayload(
				formValues,
				contractorTypeValue,
				contractorFields,
				latValue,
				lngValue
			);

			postAddSiteLeadBySSData(newSitePayload, {
				onSuccess: (data) => {
					if (data && Object.keys(data).length) {
						setSiteLeadId(data?.id);
						setCurrentStep(1);
					}
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message);
				},
			});
		},
		[
			getContractorFieldMapping,
			createNewSitePayload,
			postAddSiteLeadBySSData,
			setSiteLeadId,
			setCurrentStep,
			setToaster,
		]
	);

	const handleNext = useCallback(
		(latValue?: number | null, lngValue?: number | null): void => {
			const { contractType, competitionReason, glueUsedAtSite } = formik.values;
			const isEditMode = !!(siteData?.id && sfid);

			const contractorTypeValue = typeOfContractor.find((contractor) => contractor.value === contractType)?.name ?? '';
			const reasonForCompetition = glueUsedAtSite?.toLowerCase() === 'competition' ? competitionReason : '';

			// Create and set key base values
			// In edit mode, don't pass lat/long values
			const keyBaseValues = createKeyBaseValues(
				formik.values,
				contractorTypeValue,
				reasonForCompetition ?? '',
				isEditMode ? undefined : latValue,
				isEditMode ? undefined : lngValue,
				isEditMode
			);
			Object.entries(keyBaseValues).forEach(([key, value]) => {
				setKeyBaseValue(key, value);
			});

			// Handle update case
			if (isEditMode) {
				updateSiteLeadData();
				return;
			}

			// Handle new site creation
			handleNewSiteCreation(formik.values, contractorTypeValue, latValue, lngValue);
		},
		[sfid, formik.values, siteData, setKeyBaseValue, updateSiteLeadData, createKeyBaseValues, handleNewSiteCreation]
	);

	const handleLocationSubmit = useCallback((): void => {
		formik.setFieldValue('siteLocation', currentAddress);
		setIsLocationFromMap(true);

		// Get lat/long from address using reverse geocoding
		if (currentAddress && window.google?.maps?.Geocoder) {
			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode({ address: currentAddress }, (results, status) => {
				if (status === 'OK' && results?.[0]?.geometry?.location) {
					const location = results[0].geometry.location;
					const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
					const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
					setLatitude(lat);
					setLongitude(lng);
				} else {
					setLatitude(null);
					setLongitude(null);
				}
			});
		} else {
			setLatitude(null);
			setLongitude(null);
		}

		setLocationModal(false);
	}, [currentAddress, formik]);

	const handleOpenSiteLocationModal = (): void => {
		setLocationModal((prevState) => !prevState);
	};

	const handleCloseSiteLocationModal = (): void => {
		setLocationModal(false);
	};

	const getCurrentLocation = useCallback((): Promise<{ lat: number; lng: number } | null> => {
		return new Promise((resolve) => {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						const lat = position.coords.latitude;
						const lng = position.coords.longitude;
						setLatitude(lat);
						setLongitude(lng);
						resolve({ lat, lng });
					},
					(error) => {
						setLatitude(null);
						setLongitude(null);
						// eslint-disable-next-line no-console
						console.error('Error getting current location:', error);
						resolve(null);
					},
					{ timeout: 10000, maximumAge: 60000 }
				);
			} else {
				setLatitude(null);
				setLongitude(null);
				setToaster(true, ToastType.error, 'Geolocation is not supported by this browser.');
				resolve(null);
			}
		});
	}, [setToaster]);

	const handleSiteLocationBlur = useCallback(
		async (e: React.FocusEvent<HTMLInputElement>): Promise<void> => {
			formik.handleBlur(e);
			const addressValue = e.target.value?.trim();

			// If in edit mode and we already have coordinates, skip location permission
			if (sfid && siteData?.id && latitude && longitude) {
				return;
			}

			// If user manually typed address and location was not selected from map
			if (addressValue && !isLocationFromMap && addressValue !== currentAddress) {
				// Get current location as mandatory
				await getCurrentLocation();
			}
		},
		[formik, isLocationFromMap, currentAddress, getCurrentLocation, sfid, siteData?.id, latitude, longitude]
	);

	const handleSiteLocationChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			// If user manually changes the address, reset the map flag
			if (e.target.value !== currentAddress) {
				setIsLocationFromMap(false);
			}
		},
		[currentAddress]
	);

	const handleNextValidate = async (): Promise<void> => {
		const isEditMode = !!(sfid && siteData?.id);

		// In edit mode, skip location permission and don't pass lat/long
		if (isEditMode) {
			handleNext();
			return;
		}

		// For new sites, location validation is required
		if (!formik.values?.siteLocation) {
			handleNext();
			return;
		}

		// If location is selected from map, use map coordinates
		if (isLocationFromMap) {
			// Use coordinates from map selection (already stored in state)
			if (latitude && longitude) {
				handleNext(latitude, longitude);
			} else {
				// Fallback: try to get coordinates from address via geocoding
				if (!window.google?.maps?.Geocoder) {
					handleNext();
					return;
				}
				const geocoder = new window.google.maps.Geocoder();
				geocoder.geocode({ address: formik.values.siteLocation }, (results, status) => {
					if (status === 'OK' && results?.[0]?.geometry?.location) {
						const geoLocation = results[0].geometry.location;
						let lat: number;
						let lng: number;
						if (geoLocation instanceof google.maps.LatLng) {
							lat = geoLocation.lat();
							lng = geoLocation.lng();
						} else {
							lat = (geoLocation as google.maps.LatLngLiteral).lat;
							lng = (geoLocation as google.maps.LatLngLiteral).lng;
						}
						setLatitude(lat);
						setLongitude(lng);
						handleNext(lat, lng);
					} else {
						setToaster(true, ToastType.error, 'Failed to get coordinates. Please try again.');
					}
				});
				return;
			}
			return;
		}

		// If location is manually entered (not from map), get current location
		const location = await getCurrentLocation();
		if (!location?.lat || !location?.lng) {
			setToaster(true, ToastType.error, 'Please allow location access to continue.');
			return;
		}
		// Use current location coordinates
		handleNext(location.lat, location.lng);
	};

	const gradeOptions = useMemo(() => {
		if (!gradeList?.length) return [];
		return gradeList?.map((grade, index) => {
			return {
				id: index,
				value: grade.name,
			};
		});
	}, [gradeList]);

	const competitionOptions = useMemo(() => {
		if (!competitionList?.length) return [];
		return competitionList.map((competition, index) => {
			return {
				id: index,
				value: competition.name,
			};
		});
	}, [competitionList]);

	const secondaryCompetitionOptions = useMemo(() => {
		const options = competitionOptions.length > 0 ? [...competitionOptions] : [];
		// Add "Other" option at the end
		options.push({
			id: competitionOptions.length,
			value: 'Other',
		});
		return options;
	}, [competitionOptions]);

	const handleMultiSelectChange = (e: DropdownOptions): void => {
		const hasNA = formik.values.substratesCombinations.some((v) => v.value === 'NA');
		const isNASelected = e.value === 'NA';

		setMaxLimit(isNASelected && !hasNA ? 1 : 3);
		if (isNASelected) {
			formik.setFieldValue('substratesCombinations', hasNA ? [] : [{ id: 1, value: 'NA' }]);
		}
	};

	const hasRequestedContractorDetails = useRef<string | null>(null);

	useEffect(() => {
		if (
			phoneValidationData?.data?.exists &&
			contractorNumber &&
			contractorNumber.length === 10 &&
			!isPhoneValidationFetching &&
			!contractorDetailsResult &&
			hasRequestedContractorDetails.current !== contractorNumber
		) {
			hasRequestedContractorDetails.current = contractorNumber;

			fetchContractorDetails(
				{ phone: contractorNumber },
				{
					onSuccess: (contractorDetails: IContractorDetailsResponse) => {
						// Check if contractorDetails is valid and has required fields
						if (contractorDetails) {
							setContractorDetailsResult(contractorDetails);
							setContractorStatusMessage('Contractor already exists');

							// Set contractor name from response
							formik.setFieldValue('contractorName', contractorDetails.name);
							setKeyBaseValue('contractorName', contractorDetails.name);

							// Set membership number
							setKeyBaseValue('membershipNo', contractorDetails.membershipno);

							// Handle status-specific fields
							if (contractorDetails.status === 'lead') {
								setKeyBaseValue('appIdC', contractorDetails.app_id_c);
								setContractorLeadId(contractorDetails.app_id_c);
								setEndUserC(false);
							} else if (contractorDetails.status === 'account') {
								setContractorAppIdC(contractorDetails.sfid);
								setEndUserC(true);
							} else {
								setEndUserC(false);
							}
						} else {
							setToaster(true, ToastType.error, 'Invalid contractor details received');
						}
					},
					onError: (error) => {
						console.error('Contractor details API error:', error);
						setToaster(true, ToastType.error, error?.message);
						hasRequestedContractorDetails.current = null;
					},
				}
			);
		}
	}, [phoneValidationData?.data?.exists, contractorNumber, isPhoneValidationFetching, contractorDetailsResult]);

	// Add a new useEffect to handle when contractor doesn't exist
	useEffect(() => {
		if (
			phoneValidationData?.data?.exists === false &&
			contractorNumber &&
			contractorNumber.length === 10 &&
			!isPhoneValidationFetching &&
			!contractorDetailsResult
		) {
			setContractorStatusMessage('Contractor not exist, create new contractor');
			setEndUserC(false);
		}
	}, [phoneValidationData?.data?.exists, contractorNumber, isPhoneValidationFetching, contractorDetailsResult]);

	// Clear message when number is not 10 digits or while APIs are fetching
	useEffect(() => {
		if (
			contractorNumber.length !== 10 ||
			isPhoneValidationFetching ||
			(phoneValidationData?.data?.exists === true && isContractorDetailsPending)
		) {
			setContractorStatusMessage('');
		}
	}, [contractorNumber, isPhoneValidationFetching, phoneValidationData?.data?.exists, isContractorDetailsPending]);

	// Reset contractor details when phone number changes
	useEffect(() => {
		if (!sfid) {
			setContractorDetailsResult(null);
			setContractorAppIdC(undefined);
			setContractorLeadId(undefined);
			setEndUserC(false);
			setIsContractorCreated(false);
			hasRequestedContractorDetails.current = null;
			formik.setFieldValue('contractorName', '');
			// Clear message when number changes (will be set again if 10 digits and API completes)
			if (contractorNumber.length !== 10) {
				setContractorStatusMessage('');
			}
		}
	}, [contractorNumber, sfid]);

	// Clear "Other" text field when "Other" option is deselected
	useEffect(() => {
		const hasOther = formik.values.secondaryCompetitionReason.some((item) => item.value === 'Other');
		if (!hasOther && formik.values.secondaryCompetitionReasonOther) {
			formik.setFieldValue('secondaryCompetitionReasonOther', '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.secondaryCompetitionReason]);

	useEffect(() => {
		if (!sfid && siteDetails) {
			// Only run in add mode
			formik.setFieldValue('contractorName', siteDetails?.name || '');
			formik.setFieldValue('contractorNumber', siteDetails?.number || '');
		}
	}, [siteDetails, sfid]);

	// Helper function to get contractor details based on site stage
	const getContractorDetails = useCallback((siteData: SiteSummaryDetails) => {
		const isPendingContractorValidation = siteData.current_stage_of_site_c === 'Pending Contractor Validation';

		return {
			contractorPhone: isPendingContractorValidation
				? siteData.lead_contractor_phone ?? siteData.contract_phone
				: siteData.contract_phone,
			contractorName: isPendingContractorValidation
				? siteData.lead_contractor_name ?? siteData.contract_name
				: siteData.contract_name,
		};
	}, []);

	// Helper function to create form values from site data
	const createFormValuesFromSiteData = useCallback(
		(siteData: SiteSummaryDetails, contractorDetails: ReturnType<typeof getContractorDetails>) => {
			// Parse proposed_field_competition_sites to auto-fill secondaryCompetitionReason
			const parsedSecondaryReason = parseSecondaryCompetitionReasonFromProposedField(
				siteData.proposed_field_competition_sites,
				secondaryCompetitionOptions
			);

			return {
				...defaultAddSiteLeadFormInitialValues,
				siteName: siteData.name ?? '',
				contractorName: contractorDetails.contractorName ?? '',
				contractorNumber: contractorDetails.contractorPhone ?? '',
				contractType: typeOfContractor.find((item) => item.name === siteData.type_of_contract)?.value ?? '',
				gradeUsed: siteData.grade_glue_used_at_the_site ?? '',
				brandUsed: siteData.brand_of_glue_used_at_site ?? '',
				siteLocation: siteData.site_location_c ?? '',
				competitionReason: siteData.reason_of_competition ?? '',
				glueUsedAtSite: siteData.glue_used_at_the_site ?? '',
				secondaryCompetitionReason: parsedSecondaryReason.options,
				secondaryCompetitionReasonOther: parsedSecondaryReason.otherText ?? '',
				substratesCombinations: parseMultiselectOptions(siteData.roff_substrate_c, substratesOptions) ?? [],
				superVisorName: siteData.supervisor_name ?? '',
				superVisorNumber: siteData.supervisor_phone_number ?? '',
			};
		},
		[secondaryCompetitionOptions]
	);

	const lastSiteDataId = useRef<number | null>(null);

	useEffect(() => {
		// Only initialize form if siteData has changed (different site) or hasn't been initialized yet
		if (siteData && Object.keys(siteData).length > 0 && !isSiteDataFetching && siteData.id !== lastSiteDataId.current) {
			lastSiteDataId.current = siteData.id;
			const contractorDetails = getContractorDetails(siteData);
			const formValues = createFormValuesFromSiteData(siteData, contractorDetails);

			formik.setValues(formValues);

			// Update contractorNumber state to trigger phone validation
			if (contractorDetails.contractorPhone) {
				setContractorNumber(contractorDetails.contractorPhone);
			}

			// Set latitude and longitude from siteData if available (edit mode)
			if (sfid && siteData) {
				const siteDataWithCoords = siteData as SiteSummaryDetails & { latitude?: number; longitude?: number };
				if (siteDataWithCoords.latitude && siteDataWithCoords.longitude) {
					setLatitude(siteDataWithCoords.latitude);
					setLongitude(siteDataWithCoords.longitude);
				}
			}
		}

		// Reset the ref when siteData is cleared
		if (!siteData || Object.keys(siteData).length === 0) {
			lastSiteDataId.current = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteData?.id, isSiteDataFetching, getContractorDetails, createFormValuesFromSiteData, sfid]);

	useEffect(() => {
		if (state?.site) {
			const { site } = state;
			formik.setValues(
				{
					...formik.values,
					siteSpecialist: site.site_specialist_code,
					superVisorName: site.supervisor_name,
					superVisorNumber: site.supervisor_phone_number,
					siteArea: site.site_area,
				},
				true
			);
		}
	}, [state]);

	const getFieldError = (field: string): string | null => {
		const isTouched = getIn(formik.touched, field);
		const error = getIn(formik.errors, field);

		if (isTouched && typeof error === 'string') {
			return error;
		}

		return null;
	};

	const shouldShowLoader = (...flags: Array<boolean | undefined>): boolean => flags.some(Boolean);
	const getFirstError = (...errors: Array<Error | null | undefined>): Error | null => {
		const found = errors.find((error): error is Error => error != null);
		return found ?? null;
	};

	// Helper function to determine if "Add New Contractor" button should be disabled
	const isAddContractorButtonDisabled = useCallback((): boolean => {
		const contractorNumberValue = formik.values.contractorNumber ?? contractorNumber;
		const hasValidContractorNumber = contractorNumberValue && contractorNumberValue.length === 10;
		const contractorExists = !!contractorDetailsResult;
		const contractorAlreadyCreated = isContractorCreated;
		const hasExistingContractorData =
			!!(sfid && siteData?.id) && !!(siteData?.contract_name && siteData?.contract_phone);

		// Disable button if:
		// 1. Contractor number is not 10 digits
		if (!hasValidContractorNumber) {
			return true;
		}

		// 2. Contractor already created or has existing data in edit mode
		if (contractorAlreadyCreated || hasExistingContractorData) {
			return true;
		}

		// 3. Phone validation API is still fetching - wait for it to complete
		if (isPhoneValidationFetching) {
			return true;
		}

		// 4. Phone validation hasn't returned a result yet (waiting for initial response)
		//    Check if we have data, and if not, disable button until we get a response
		const hasPhoneValidationResult = phoneValidationData !== undefined && phoneValidationData !== null;
		if (!hasPhoneValidationResult) {
			return true;
		}

		// 5. If phone exists (exists === true), we need to check contractor details
		//    Disable if contractor details API is still pending
		if (phoneValidationData?.data?.exists === true && isContractorDetailsPending) {
			return true;
		}

		// 6. If phone exists but contractor details haven't been fetched yet
		//    This means we're waiting for the useEffect to trigger the API call
		if (
			phoneValidationData?.data?.exists === true &&
			!isContractorDetailsPending &&
			!contractorDetailsResult &&
			hasRequestedContractorDetails.current !== contractorNumberValue
		) {
			return true;
		}

		// 7. If phone exists and contractor details API was called but we're still waiting for response
		//    (API called but no result yet)
		//    BUT if API errored, allow adding new contractor (enable button)
		if (
			phoneValidationData?.data?.exists === true &&
			!contractorDetailsResult &&
			hasRequestedContractorDetails.current === contractorNumberValue &&
			!isContractorDetailsError
		) {
			return true;
		}

		// 8. Contractor exists - disable button
		if (contractorExists) {
			return true;
		}

		// Enable button only when:
		// - Contractor number is 10 digits
		// - Phone validation is complete and exists === false (contractor doesn't exist)
		// - OR phone exists but contractor details API is complete and contractor does NOT exist
		// - Contractor not already created
		return false;
	}, [
		formik.values.contractorNumber,
		contractorNumber,
		contractorDetailsResult,
		isContractorCreated,
		isPhoneValidationFetching,
		isContractorDetailsPending,
		isContractorDetailsError,
		phoneValidationData,
		sfid,
		siteData?.id,
		siteData?.contract_name,
		siteData?.contract_phone,
	]);

	return (
		<section className="new-site-section">
			<Wrapper
				isLoaderVisible={shouldShowLoader(isGradeListPending, iscompetitionListPending, isSiteDataFetching)}
				isError={getFirstError(gradeListError, competitionListError, siteDataError)}
			>
				<div className="container">
					<form onSubmit={formik.handleSubmit}>
						<ul className="new-site-wrapper row">
							<li className="form-control d-col d-col-2">
								<TextInput
									id="siteName"
									name="siteName"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.siteName ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site Name"
									error={getFieldError('siteName')}
									disabled={!!(sfid && siteData?.name)}
									isAutoFocus
									required
								/>
							</li>
							<li className="form-control d-col d-col-2 location">
								<TextInput
									id="siteLocation"
									name="siteLocation"
									onBlur={handleSiteLocationBlur}
									onChange={handleSiteLocationChange}
									type="text"
									value={formik.values.siteLocation ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site Location"
									error={getFieldError('siteLocation')}
									disabled={!!(sfid && siteData?.site_location_c)}
									required
								/>
								<button
									type="button"
									aria-label="map link"
									className="icon-location map"
									onClick={handleOpenSiteLocationModal}
									disabled={!!(sfid && siteData?.site_location_c)}
								/>
							</li>
							<li className="d-col d-col-2 form-radio">
								<RadioButton
									name="glueUsedAtSite"
									title="Glue Used at Site"
									checked={formik.values.glueUsedAtSite ?? ''}
									radioOptions={glueUsedRadioTypeList}
									onChange={formik.handleChange}
									isDisabled={!!(sfid && siteData?.glue_used_at_the_site)}
									required
								/>
							</li>

							{formik.values.glueUsedAtSite === 'Competition' && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="brandUsed"
										name="brandUsed"
										label="Brand of Glue used at Site"
										title={formik.values.brandUsed ?? ''}
										options={brandUsedList}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.brand_of_glue_used_at_site)}
										onBlur={formik.handleBlur}
										onChange={(e) => {
											formik.setFieldValue('brandUsed', e.value);
											const selectedBrand = brandUsedList.find((item) => item.value === e.value);
											formik.setFieldValue('gradeUsed', selectedBrand?.grade ?? '');
										}}
										required
									/>
								</li>
							)}

							<li className="d-col d-col-2">
								<Dropdown
									id="gradeUsed"
									name="gradeUsed"
									label="Grade of Glue used at Site"
									onBlur={formik.handleBlur}
									title={formik.values.gradeUsed ?? ''}
									error={getFieldError('gradeUsed')}
									options={gradeOptions}
									setFieldValue={formik.setFieldValue}
									disabled={
										!!(sfid && siteData?.grade_glue_used_at_the_site) || formik.values.glueUsedAtSite === 'Competition'
									}
									required
								/>
							</li>

							<li className="d-col d-col-2">
								<Dropdown
									id="contractType"
									name="contractType"
									label="Type of Contract"
									onBlur={formik.handleBlur}
									title={formik.values.contractType ?? ''}
									error={getFieldError('contractType')}
									options={typeOfContractor}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>
							<li className="d-col d-col-2 single-line-multiselect">
								<MultiselectDropdown
									id="substratesCombinations"
									name="substratesCombinations"
									label="Are any of the below substrates combinations being used at site?"
									className="input-border single-lable-multi-dropdown"
									onBlur={formik.handleBlur}
									values={formik.values.substratesCombinations}
									options={substratesOptions}
									setFieldValue={formik.setFieldValue}
									maxLimit={maxLimit}
									onChange={handleMultiSelectChange}
									required
									error={
										formik.touched.substratesCombinations && formik.errors.substratesCombinations
											? formik.errors.substratesCombinations.toString()
											: null
									}
								/>
							</li>

							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' && (
								<li className="d-col d-col-2">
									<Dropdown
										id="competitionReason"
										name="competitionReason"
										label="Primary Reason For Competition"
										onBlur={formik.handleBlur}
										title={formik.values.competitionReason ?? ''}
										error={getFieldError('competitionReason')}
										options={competitionOptions}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.reason_of_competition)}
										required
									/>
								</li>
							)}
							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="secondaryCompetitionReason"
										name="secondaryCompetitionReason"
										label="Secondary Reason For Competition"
										onBlur={formik.handleBlur}
										values={formik.values.secondaryCompetitionReason}
										error={
											formik.touched.secondaryCompetitionReason && formik.errors.secondaryCompetitionReason
												? formik.errors.secondaryCompetitionReason.toString()
												: null
										}
										options={secondaryCompetitionOptions}
										setFieldValue={formik.setFieldValue}
										disabled={!!(sfid && siteData?.proposed_field_competition_sites)}
									/>
								</li>
							)}
							{formik.values.glueUsedAtSite?.toLowerCase() === 'competition' &&
								formik.values.secondaryCompetitionReason.some((item) => item.value === 'Other') && (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="secondaryCompetitionReasonOther"
											name="secondaryCompetitionReasonOther"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.secondaryCompetitionReasonOther ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Please specify other reason"
											error={
												formik.touched.secondaryCompetitionReasonOther && formik.errors.secondaryCompetitionReasonOther
													? formik.errors.secondaryCompetitionReasonOther
													: null
											}
											disabled={!!(sfid && siteData?.proposed_field_competition_sites)}
											required
										/>
									</li>
								)}

							<li className="form-control d-col d-col-2">
								<TextInput
									id="superVisorName"
									name="superVisorName"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.superVisorName ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site In-Charge Name"
									disabled={!!(sfid && siteData?.supervisor_name)}
									error={getFieldError('superVisorName')}
									required
								/>
							</li>

							<li className="form-control d-col d-col-2">
								<TextInput
									id="superVisorNumber"
									name="superVisorNumber"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.superVisorNumber ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Site In-Charge Number"
									disabled={!!(sfid && siteData?.supervisor_phone_number)}
									error={getFieldError('superVisorNumber')}
									maxLength={10}
									required
								/>
							</li>

							<li className="form-control d-col d-col-2 contractor-submit-wrapper">
								<TextInput
									id="contractorNumber"
									name="contractorNumber"
									type="tel"
									onBlur={formik.handleBlur}
									value={formik.values.contractorNumber ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Contractor Number"
									error={
										formik.touched.contractorNumber && formik.errors.contractorNumber
											? formik.errors.contractorNumber
											: null
									}
									maxLength={10}
									onChange={handlePrimaryNumberChange}
									required
									disabled={!!sfid}
								/>

								<div className="contractor-button-wrapper">
									<div>
										{contractorNumber.length === 10 && contractorStatusMessage && (
											<div className={`contractor-status-message ${contractorDetailsResult ? 'exists' : 'not-exists'}`}>
												{contractorStatusMessage}
											</div>
										)}
									</div>

									{!sfid && (
										<button
											type="button"
											aria-label="add new contractor"
											className="btn btn-primary"
											disabled={isAddContractorButtonDisabled()}
											onClick={() => setIsContractorModalOpen(true)}
										>
											Add New Contractor
										</button>
									)}
								</div>
							</li>

							<li className="form-control d-col d-col-2">
								<TextInput
									id="contractorName"
									name="contractorName"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.contractorName ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Contractor Name"
									error={getFieldError('contractorName')}
									disabled
									required
								/>
							</li>
						</ul>
					</form>
					<Modal
						modalId="googleMapModal"
						className="map-popup dialog-sm md-large pt-4"
						modalOpen={locationModal}
						onModalClose={handleCloseSiteLocationModal}
					>
						<GoogleMapModal handleConfirm={handleLocationSubmit} handleClose={handleCloseSiteLocationModal} />
					</Modal>
					<Modal
						modalTitle="Contractor Details"
						modalOpen={isContractorModalOpen}
						modalId="contractorDetailsModal"
						isEscAllow={false}
						className="dialog-lg dialog-bottom dialog-conform"
					>
						<ContractorSSDetailsModal
							isContractorExists={phoneValidationData?.data?.exists ?? false}
							name={formik.values.contractorName ?? ''}
							phone={formik.values.contractorNumber ?? ''}
							contractorDetails={contractorDetailsResult || null}
							onCancel={() => setIsContractorModalOpen(false)}
							onSubmit={(name: string, appIdC?: string) => {
								// Set form values
								formik.setFieldValue('contractorName', name);
								setKeyBaseValue('contractorName', name);

								// Mark contractor as created and update status message
								setIsContractorCreated(true);
								setContractorStatusMessage('New contractor created successfully');

								if (appIdC) {
									setContractorAppIdC(appIdC);
									setKeyBaseValue('appIdC', appIdC);
								}

								// Close the modal
								setIsContractorModalOpen(false);
							}}
						/>
					</Modal>
					<Stepper nextHandler={handleNextValidate} isNext={!formik.isValid || !formik.dirty} totalSteps={2} />
				</div>
			</Wrapper>
		</section>
	);
};
